<script lang="ts">
  import Modal from '../../ui/Modal.svelte';
  import { roomsQuery } from '$lib/queries/rooms';
  import { zonesQuery } from '$lib/queries/zones';
  import { loadsQuery } from '$lib/queries/loads';
  import { unitsQuery } from '$lib/queries/units';
  import { createSceneMutation } from '$lib/mutations/scenes';
  import { sceneLoadsQuery } from '$lib/queries/scene_loads';
  import { createSceneLoad } from '$lib/api/scene_load';
  import { createInputAction, updateInputAction } from '$lib/api/input_action';
  import { invalidateAfterSceneLoadUpdate, invalidateAfterInputDeviceUpdate } from '$lib/state/invalidation';
  import { sceneLoadTargetId } from '$lib/utils/scene_load';
  import { router } from '$lib/router';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import type { InputDeviceResponse } from '$lib/types/input_device';

  type PrefillItem = { load: number; value: number };

  let {
    open = false,
    keypad = null,
    keyId = null,
    eventType = 'on',
    existingActionId = null,
    existingSceneId = null,
    existingSceneName = null,
    existingScope = null,
    existingScopeId = null,
    existingLoads = null,
    prefill = null,
    buttonLabel = '',
    onclose = undefined,
    onsaved = undefined
  }: {
    open?: boolean;
    keypad?: InputDeviceResponse | null;
    keyId?: number | null;
    eventType?: 'on' | 'off';
    existingActionId?: number | null;
    existingSceneId?: number | null;
    existingSceneName?: string | null;
    existingScope?: string | null;
    existingScopeId?: number | null;
    existingLoads?: PrefillItem[] | null;
    prefill?: PrefillItem[] | null;
    buttonLabel?: string;
    onclose?: () => void;
    onsaved?: (_detail: { eventType: string; sceneId: number; loadIds: number[] }) => void;
  } = $props();

  const unitId = $derived(router.unitId ?? 0);
  const unitRooms = roomsQuery();
  const unitZones = zonesQuery();
  const unitLoads = loadsQuery();
  const units = unitsQuery();
  const unit = $derived((units.data ?? []).find((u) => u.id === unitId) ?? null);

  const createScene = createSceneMutation();
  const existingSceneLoadsQ = sceneLoadsQuery(() => existingSceneId ?? 0);

  const resolvedExistingLoads = $derived.by((): PrefillItem[] => {
    if (existingSceneId && (existingSceneLoadsQ.data ?? []).length > 0) {
      return (existingSceneLoadsQ.data ?? []).map((sl) => ({
        load: sceneLoadTargetId(sl),
        value: sl.value ?? 100
      }));
    }
    return existingLoads ?? [];
  });

  let scope = $state<'room' | 'zone' | 'unit'>('room');
  let scopeId = $state<number | null>(null);
  let sceneName = $state('');
  let selections = $state<Record<number, number>>({});
  let initializedFor = $state<string | null>(null);

  const scopeOptions = $derived({
    room: (unitRooms.data ?? []).map((room) => {
      const zone = (unitZones.data ?? []).find((z) => z.id === room.zone);
      return { id: room.id, name: `${zone?.name ?? 'Zone'} → ${room.name}` };
    }),
    zone: (unitZones.data ?? []).map((zone) => ({ id: zone.id, name: zone.name })),
    unit: unit ? [{ id: unit.id, name: unit.name }] : []
  });

  function loadsForScope(s: string, sId: number | null) {
    if (sId == null) return [];
    if (s === 'zone') {
      const roomIds = (unitRooms.data ?? []).filter((r) => r.zone === sId).map((r) => r.id);
      return (unitLoads.data ?? []).filter((l) => roomIds.includes(l.room));
    }
    if (s === 'room') return (unitLoads.data ?? []).filter((l) => l.room === sId);
    return unitLoads.data ?? [];
  }

  const previewLoads = $derived(loadsForScope(scope, scopeId));

  $effect(() => {
    const loadCount = resolvedExistingLoads.length;
    const initKey = `${keyId}:${eventType}:${existingSceneId ?? ''}:${loadCount}`;
    if (open && initializedFor !== initKey) {
      initializedFor = initKey;
      scope = 'room';
      scopeId = keypad?.location_room ?? null;
      selections = {};
      sceneName = '';
      if (prefill && prefill.length) {
        scope = 'unit';
        scopeId = unitId || scopeId;
        for (const p of prefill) selections[p.load] = p.value;
        selections = { ...selections };
      } else if (existingSceneId && loadCount > 0) {
        scope = (existingScope as 'room' | 'zone' | 'unit') ?? 'room';
        scopeId = existingScopeId ?? scopeId;
        for (const p of resolvedExistingLoads) selections[p.load] = p.value;
        selections = { ...selections };
        sceneName = existingSceneName ?? '';
      }
      if (!sceneName) sceneName = suggestName();
    }
    if (!open) initializedFor = null;
  });

  function suggestName() {
    const where =
      scope === 'room'
        ? (unitRooms.data ?? []).find((r) => r.id === scopeId)?.name
        : scope === 'zone'
          ? (unitZones.data ?? []).find((z) => z.id === scopeId)?.name
          : unit?.name;
    return `${where ?? 'Scene'} — ${buttonLabel} ${eventType === 'on' ? 'On' : 'Off'}`;
  }

  function toggle(loadId: number) {
    if (loadId in selections) {
      const next = { ...selections };
      delete next[loadId];
      selections = next;
    } else {
      selections = { ...selections, [loadId]: 100 };
    }
  }

  function setValue(loadId: number, value: number) {
    selections = { ...selections, [loadId]: Math.max(0, Math.min(100, +value)) };
  }

  function allOn() {
    const next: Record<number, number> = {};
    for (const l of previewLoads) next[l.id] = l.load_type === 'curtain' ? 0 : 100;
    selections = next;
  }

  function allOff() {
    const next: Record<number, number> = {};
    for (const l of previewLoads) next[l.id] = 0;
    selections = next;
  }

  const saving = $derived(createScene.isPending);

  async function save() {
    const loadIds = Object.keys(selections).map(Number);
    if (!unitId || !keyId || loadIds.length === 0 || !sceneName.trim()) {
      toast.error('Pick at least one load and a name');
      return;
    }
    try {
      const scene = await createScene.mutateAsync({
        unit: unitId,
        name: sceneName.trim(),
        scope,
        scope_id: scopeId!,
        priority: 1
      });
      for (const loadId of loadIds) {
        await createSceneLoad({
          scene: scene.id,
          load: loadId,
          value: selections[loadId],
          fade_ms: 2000,
          delay_ms: 0
        });
      }
      if (existingActionId) {
        await updateInputAction(existingActionId, { scene: scene.id });
      } else {
        await createInputAction({
          key: keyId!,
          event_type: eventType,
          scene: scene.id
        });
      }
      invalidateAfterSceneLoadUpdate(unitId, scene.id);
      invalidateAfterInputDeviceUpdate(unitId);
      toast.success(`${eventType === 'on' ? 'On' : 'Off'} action saved`);
      onsaved?.({ eventType, sceneId: scene.id, loadIds });
      onclose?.();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  }
</script>

<Modal {open} size="xl" onclose={() => onclose?.()}>
  {#snippet header()}
    <h3 class="text-base font-medium text-text-primary">
      {buttonLabel} · {eventType === 'on' ? 'On action' : 'Off action'}
    </h3>
    <p class="text-xs text-text-muted mt-0.5">
      Pick the loads this button controls and their levels. Saving creates a scene and assigns it to
      the button.
    </p>
  {/snippet}

  {#snippet children()}
    <div class="space-y-4">
      {#if prefill && prefill.length}
        <div
          class="rounded border border-accent-primary/40 bg-bg-selected px-3 py-2 text-xs text-text-secondary"
        >
          Suggested opposite of the On action — every controlled load set to off. Adjust as needed.
        </div>
      {/if}

      <div class="flex flex-wrap items-center gap-2">
        <span class="text-2xs uppercase tracking-wide text-text-muted">Scope</span>
        <div class="inline-flex rounded border border-border-default p-0.5 text-xs">
          {#each ['room', 'zone', 'unit'] as s}
            <button
              type="button"
              class="px-2.5 py-1 rounded capitalize transition-colors duration-100 {scope === s
                ? 'bg-text-primary text-bg-base'
                : 'text-text-secondary hover:text-text-primary'}"
              onclick={() => {
                scope = s as 'room' | 'zone' | 'unit';
                scopeId = scopeOptions[s as keyof typeof scopeOptions][0]?.id ?? null;
              }}
            >
              {s}
            </button>
          {/each}
        </div>
        <select
          class="rounded border border-border-strong bg-bg-canvas px-2 py-1 text-sm text-text-primary"
          bind:value={scopeId}
        >
          {#each scopeOptions[scope] as opt (opt.id)}
            <option value={opt.id}>{opt.name}</option>
          {/each}
        </select>
      </div>

      <div class="flex items-center gap-2">
        <button type="button" class="btn-secondary text-xs" onclick={allOn}>All on</button>
        <button type="button" class="btn-secondary text-xs" onclick={allOff}>All off</button>
        <span class="text-xs text-text-muted ml-1">
          {Object.keys(selections).length} load{Object.keys(selections).length === 1 ? '' : 's'} selected
          {#if previewLoads.length > 0}
            ({previewLoads.filter((l) => l.id in selections).length} in this {scope})
          {/if}
        </span>
      </div>

      {#if previewLoads.length === 0}
        <p class="text-sm text-text-muted">No loads in this scope.</p>
      {:else}
        <div
          class="border border-border-default rounded divide-y divide-border-default max-h-72 overflow-y-auto"
        >
          {#each previewLoads as load (load.id)}
            {@const included = load.id in selections}
            <div class="flex items-center gap-3 px-3 py-2">
              <input type="checkbox" checked={included} onchange={() => toggle(load.id)} />
              <span class="flex-1 text-sm text-text-primary truncate">{load.name}</span>
              <span class="text-2xs text-text-muted">{load.load_type}</span>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={selections[load.id] ?? 0}
                disabled={!included}
                class="w-32"
                oninput={(e) => setValue(load.id, e.currentTarget.valueAsNumber)}
              />
              <span class="w-10 text-right font-mono text-xs text-text-secondary">
                {included ? `${selections[load.id]}%` : '—'}
              </span>
            </div>
          {/each}
        </div>
      {/if}

      <div>
        <div class="text-2xs uppercase tracking-wide text-text-muted mb-1">Scene name</div>
        <input
          class="w-full rounded border border-border-strong bg-bg-canvas px-2 py-1.5 text-sm text-text-primary"
          bind:value={sceneName}
        />
      </div>
    </div>
  {/snippet}

  {#snippet footer()}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-secondary text-sm" onclick={() => onclose?.()}>
        Cancel
      </button>
      <button
        type="button"
        class="btn-primary text-sm"
        disabled={saving || Object.keys(selections).length === 0}
        onclick={save}
      >
        {saving ? 'Saving…' : 'Save action'}
      </button>
    </div>
  {/snippet}
</Modal>
